import { useEffect, useState } from "react";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiError, downloadFile } from "@/lib/api";

const ALL = "all";

/** The contacts sheet as Excel — the unit in view, or every unit — for anyone who can read the page. */
export const ContactsDownload = ({ path, units, current, name, note, testId }) => {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState(ALL);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setScope(current || ALL);
  }, [open, current]);

  const get = async () => {
    setBusy(true);
    try {
      const all = scope === ALL;
      await downloadFile(path, all ? {} : { unit: scope }, `${name}_${all ? "All_Units" : scope}.xlsx`);
      setOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        className="h-11 rounded-none font-display text-[11px] font-bold uppercase tracking-widest"
        onClick={() => setOpen(true)}
        data-testid={`${testId}-button`}
      >
        <FileSpreadsheet className="mr-2 h-4 w-4" /> Contacts (.xlsx)
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-none" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black">Download contacts</DialogTitle>
            <DialogDescription>{note}</DialogDescription>
          </DialogHeader>
          <label className="block">
            <span className="label-caps">Sheet for</span>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="mt-1 h-11 rounded-none" data-testid={`${testId}-scope`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72 rounded-none bg-popover">
                <SelectItem value={ALL} data-testid={`${testId}-scope-option-all`}>
                  All units
                </SelectItem>
                {units.map((u) => (
                  <SelectItem key={u.code} value={u.code} data-testid={`${testId}-scope-option-${u.code}`}>
                    {u.code} — {u.name}
                    {u.code === current ? " (current)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <Button
            className="h-12 rounded-none font-display text-xs font-bold uppercase tracking-widest"
            disabled={busy}
            onClick={get}
            data-testid={`${testId}-submit`}
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}
            Download Excel
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};
